// ============================================================================
// CLEO'S SECRET — ClinicalDashboard
// File: src/pages/ClinicalDashboard.jsx
//
// Robyn's home workstation. Inherits the operational pattern established
// by RetentionPage: obsidian header chrome + cream main canvas + two-pane
// workspace. Extended with a 5-card stats strip and filter+search controls
// for the patient list.
//
// Header navigates to:
//   • retention (Patient Outreach)
//   • pathology-review (Pathology queue)
//   • landing (Exit clinical chrome)
//
// API calls preserved:
//   • /api/get-clinical-data   — patients + stats
//   • /api/get-pending-pathology — pathology queue stats
//   • /api/get-retention-triggers — outreach queue stats
//   • /api/get-patient-detail   — per-patient detail load
//   • /api/mark-reviewed       — intake review action
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  FF, FS, P, T,
  Spinner,
} from '../lib/design-tokens';
import RequireAdmin from '../components/RequireAdmin';

// ───────── Helpers ─────────
function formatDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}
function formatDateLong(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
}
function timeAgo(s) {
  if (!s) return '';
  var diff = Date.now() - new Date(s).getTime();
  var hours = Math.floor(diff / (1000 * 60 * 60));
  var days = Math.floor(hours / 24);
  if (hours < 1) return 'just now';
  if (hours < 24) return hours + 'h ago';
  if (days === 1) return 'yesterday';
  if (days < 7) return days + ' days ago';
  return formatDate(s);
}

// ───────── Patient display name ─────────
function patientName(patient) {
  if (!patient) return 'Unknown patient';
  if (patient.first_name && patient.last_name) {
    return patient.first_name + ' ' + patient.last_name;
  }
  return patient.first_name || patient.email || 'Unknown patient';
}

// ───────── SectionLabel (used inside detail pane) ─────────
function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: FF,
      fontSize: 'clamp(11px, 0.95vw, 13px)',
      fontWeight: 700,
      letterSpacing: '0.32em',
      textTransform: 'uppercase',
      color: P.BR2,
      marginBottom: 'clamp(14px, 1.6vw, 18px)',
      paddingBottom: 'clamp(10px, 1.2vw, 14px)',
      borderBottom: '1px solid ' + P.SAND3,
    }}>{children}</div>
  );
}

// ───────── Field — label + value pair in clinical summary ─────────
function Field({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 'clamp(14px, 1.4vw, 18px)' }}>
      <div style={{
        fontFamily: FF,
        fontSize: 'clamp(10px, 0.85vw, 11px)',
        fontWeight: 700,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        color: P.BR2,
        marginBottom: 6,
      }}>{label}</div>
      <div style={{
        fontFamily: FF,
        fontSize: 'clamp(14px, 1.2vw, 15px)',
        color: P.TX,
        lineHeight: 1.7,
      }}>{value}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// ClinicalDashboard — outer wrapper. Gates on patients.is_admin via
// RequireAdmin so the inner page (and its data-fetching useEffects) only
// mount when the caller is confirmed admin. See src/components/RequireAdmin.jsx.
// ═════════════════════════════════════════════════════════════════════════
export default function ClinicalDashboard(props) {
  return (
    <RequireAdmin email={props.email} isAdmin={props.isAdmin} onNavigate={props.onNavigate}>
      <ClinicalDashboardInner onNavigate={props.onNavigate} />
    </RequireAdmin>
  );
}

function ClinicalDashboardInner(props) {
  var onNavigate = props.onNavigate;

  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];

  // Phase 2: Pre-enrolment queue state. Loaded independently of patient data.
  var recsState = useState([]);
  var recs = recsState[0];
  var setRecs = recsState[1];
  var recsLoadingState = useState(true);
  var recsLoading = recsLoadingState[0];
  var setRecsLoading = recsLoadingState[1];
  var recsErrorState = useState(null);
  var recsError = recsErrorState[0];
  var setRecsError = recsErrorState[1];
  var recsRefreshState = useState(0);
  var recsRefresh = recsRefreshState[0];
  var setRecsRefresh = recsRefreshState[1];
  var rerunningIdState = useState(null);
  var rerunningId = rerunningIdState[0];
  var setRerunningId = rerunningIdState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var filterState = useState('review');
  var filter = filterState[0];
  var setFilter = filterState[1];

  var searchState = useState('');
  var search = searchState[0];
  var setSearch = searchState[1];

  var selectedState = useState(null);
  var selected = selectedState[0];
  var setSelected = selectedState[1];

  var detailState = useState(null);
  var detail = detailState[0];
  var setDetail = detailState[1];

  var detailLoadingState = useState(false);
  var detailLoading = detailLoadingState[0];
  var setDetailLoading = detailLoadingState[1];

  var pendingPathState = useState(0);
  var pendingPath = pendingPathState[0];
  var setPendingPath = pendingPathState[1];

  var outreachStatsState = useState({ total: 0, high: 0 });
  var outreachStats = outreachStatsState[0];
  var setOutreachStats = outreachStatsState[1];

  function loadData() {
    setLoading(true);

    fetch('/api/get-clinical-data', { method: 'POST' })
      .then(function(res) {
        if (!res.ok) throw new Error('Could not load data');
        return res.json();
      })
      .then(function(result) { setData(result); setLoading(false); })
      .catch(function(err) { setError(err.message); setLoading(false); });

    fetch('/api/get-pending-pathology', { method: 'POST' })
      .then(function(res) { return res.json(); })
      .then(function(result) {
        var stats = result && result.stats;
        setPendingPath(stats ? stats.awaiting_review : 0);
      })
      .catch(function() {});

    fetch('/api/get-retention-triggers', { method: 'POST' })
      .then(function(res) { return res.json(); })
      .then(function(result) {
        if (result.stats) setOutreachStats(result.stats);
      })
      .catch(function() {});
  }

  useEffect(function() { loadData(); }, []);

  // Phase 2: Load Pre-enrolment queue (draft recommendations). Independent of
  // loadData so it can refresh after a rerun without re-fetching everything.
  useEffect(function() {
    var cancelled = false;
    setRecsLoading(true);
    setRecsError(null);
    fetch('/api/clinical-list-recommendations?status=draft')
      .then(function(r) { return r.json(); })
      .then(function(payload) {
        if (cancelled) return;
        if (payload && payload.recommendations) {
          setRecs(payload.recommendations);
        } else if (payload && payload.error) {
          setRecsError(payload.error);
        }
        setRecsLoading(false);
      })
      .catch(function() {
        if (cancelled) return;
        setRecsError('Network error loading recommendations.');
        setRecsLoading(false);
      });
    return function() { cancelled = true; };
  }, [recsRefresh]);

  function rerunAutofill(patternAssignmentId, recommendationId) {
    if (!patternAssignmentId) return;
    setRerunningId(recommendationId);
    setRecsError(null);
    fetch('/api/recommendation-autofill', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ patternAssignmentId: patternAssignmentId }),
    })
      .then(function(r) { return r.json(); })
      .then(function(result) {
        setRerunningId(null);
        if (result && result.error) {
          setRecsError(result.error);
        } else {
          setRecsRefresh(function(n) { return n + 1; });
        }
      })
      .catch(function() {
        setRerunningId(null);
        setRecsError('Network error during rerun.');
      });
  }

  function selectPatient(patient) {
    setSelected(patient);
    setDetailLoading(true);
    setDetail(null);
    fetch('/api/get-patient-detail', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ patientId: patient.id }),
    })
      .then(function(res) { return res.json(); })
      .then(function(result) { setDetail(result); setDetailLoading(false); })
      .catch(function() { setDetailLoading(false); });
  }

  function markReviewed(intakeId) {
    fetch('/api/mark-reviewed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ intakeId: intakeId, reviewedBy: 'robyn' }),
    })
      .then(function() {
        loadData();
        if (selected) selectPatient(selected);
      });
  }

  function openCopilot() {
    if (selected && onNavigate) {
      onNavigate('copilot', { patientId: selected.id });
    }
  }

  // ────────────────────────────────────────────────────────────────────────
  // LOADING STATE
  // ────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        background: P.OBS,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner color={P.GO2} size={48} />
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // ERROR STATE
  // ────────────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div style={{
        background: P.OBS,
        color: P.IVY,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(24px, 4vw, 60px)',
        textAlign: 'center',
        fontFamily: FF,
      }}>
        <div style={{ maxWidth: 480 }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.36em',
            textTransform: 'uppercase',
            color: P.GO2,
            marginBottom: 18,
          }}>Could not load</div>
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(22px, 2.4vw, 32px)',
            fontStyle: 'italic',
            color: P.GO2,
            lineHeight: 1.5,
            marginBottom: 'clamp(28px, 3vw, 40px)',
          }}>{error}</p>
          <button
            onClick={loadData}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '14px 32px',
              background: P.GO,
              color: P.OBS,
              border: 'none',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.background = P.GO2; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = P.GO; }}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  var patients = (data && data.patients) || [];
  var stats = (data && data.stats) || { total: 0, needsReview: 0, upcoming: 0 };

  var filtered = patients.filter(function(p) {
    if (filter === 'review' && !p.needsReview) return false;
    if (filter === 'upcoming' && !p.nextBooking) return false;
    if (search) {
      var s = search.toLowerCase();
      var nameMatch = (p.first_name || '').toLowerCase().indexOf(s) >= 0 || (p.last_name || '').toLowerCase().indexOf(s) >= 0;
      var emailMatch = (p.email || '').toLowerCase().indexOf(s) >= 0;
      var concernMatch = (p.primaryConcern || '').toLowerCase().indexOf(s) >= 0;
      if (!nameMatch && !emailMatch && !concernMatch) return false;
    }
    return true;
  });

  // ────────────────────────────────────────────────────────────────────────
  // STAT CARD (inline, closure over palette)
  // ────────────────────────────────────────────────────────────────────────
  function StatCard({ label, value, highlight, color, onClick, subtitle }) {
    var clickable = !!onClick;
    return (
      <div
        onClick={onClick}
        style={{
          flex: 1,
          minWidth: 160,
          padding: 'clamp(18px, 2vw, 24px)',
          background: highlight ? 'rgba(201, 169, 97, 0.08)' : P.PAP,
          border: '1px solid ' + (highlight ? P.GO : P.SAND3),
          borderTop: '2px solid ' + (highlight ? P.GO : P.SAND3),
          cursor: clickable ? 'pointer' : 'default',
          transition: 'background 0.2s',
        }}
        onMouseEnter={function(e) {
          if (clickable && !highlight) e.currentTarget.style.background = 'rgba(232,221,196,0.5)';
        }}
        onMouseLeave={function(e) {
          if (clickable && !highlight) e.currentTarget.style.background = P.PAP;
        }}>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(10px, 0.85vw, 12px)',
          fontWeight: 700,
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 10,
        }}>{label}</div>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(28px, 3vw, 40px)',
          fontWeight: 400,
          lineHeight: 1,
          letterSpacing: '-0.015em',
          color: color || P.INK,
        }}>{value}</div>
        {subtitle && (
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(12px, 1vw, 13px)',
            fontStyle: 'italic',
            color: P.BR2,
            marginTop: 8,
            lineHeight: 1.4,
          }}>{subtitle}</div>
        )}
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // PATIENT CARD (list item, left pane)
  // ────────────────────────────────────────────────────────────────────────
  function PatientCard({ patient }) {
    var isSelected = selected && selected.id === patient.id;
    var truncatedConcern = patient.primaryConcern && patient.primaryConcern.length > 80
      ? patient.primaryConcern.substring(0, 80) + '\u2026'
      : patient.primaryConcern;

    return (
      <div
        onClick={function() { selectPatient(patient); }}
        style={{
          padding: 'clamp(16px, 1.6vw, 20px)',
          background: isSelected ? 'rgba(201, 169, 97, 0.08)' : P.PAP,
          border: '1px solid ' + (isSelected ? P.GO : P.SAND3),
          borderLeft: '3px solid ' + (patient.needsReview ? P.GO : (isSelected ? P.GO : P.SAND3)),
          cursor: 'pointer',
          marginBottom: 10,
          position: 'relative',
          transition: 'background 0.2s, border-color 0.2s',
        }}
        onMouseEnter={function(e) {
          if (!isSelected) e.currentTarget.style.background = 'rgba(232,221,196,0.5)';
        }}
        onMouseLeave={function(e) {
          if (!isSelected) e.currentTarget.style.background = P.PAP;
        }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 8,
        }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(15px, 1.3vw, 17px)',
            fontWeight: 500,
            color: P.INK,
            lineHeight: 1.2,
            letterSpacing: '-0.005em',
            flex: 1,
            minWidth: 0,
          }}>{patientName(patient)}</div>
          {patient.needsReview && (
            <span style={{
              fontFamily: FF,
              fontSize: 'clamp(9px, 0.75vw, 10px)',
              fontWeight: 700,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: P.BR2,
              flexShrink: 0,
            }}>New</span>
          )}
        </div>

        <div style={{
          fontFamily: FS,
          fontSize: 'clamp(11px, 0.95vw, 12px)',
          color: P.TF,
          marginBottom: 8,
          letterSpacing: '0.02em',
          wordBreak: 'break-word',
        }}>{patient.email}</div>

        {truncatedConcern && (
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(13px, 1.1vw, 14px)',
            fontStyle: 'italic',
            color: P.TL,
            lineHeight: 1.6,
            margin: '0 0 10px',
          }}>&ldquo;{truncatedConcern}&rdquo;</p>
        )}

        <div style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          fontFamily: FS,
          fontSize: 'clamp(10px, 0.85vw, 11px)',
          letterSpacing: '0.04em',
          color: P.TF,
        }}>
          {patient.latestIntake && (
            <span style={{ fontStyle: 'italic' }}>
              Intake {timeAgo(patient.latestIntake.completed_at || patient.latestIntake.created_at)}
            </span>
          )}
          {patient.nextBooking && (
            <span style={{
              color: P.BR2,
              fontWeight: 600,
              fontFamily: FF,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}>Next {formatDate(patient.nextBooking.appointment_date)}</span>
          )}
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // DETAIL PANE
  // ────────────────────────────────────────────────────────────────────────
  function renderDetail() {
    if (!selected) {
      return (
        <div style={{
          padding: 'clamp(60px, 8vw, 100px) clamp(24px, 3vw, 40px)',
          textAlign: 'center',
          background: 'rgba(232,221,196,0.3)',
          border: '1px dashed ' + P.SAND3,
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <p style={{
            ...T.premise,
            fontSize: 'clamp(18px, 1.8vw, 24px)',
            color: P.BR2,
            maxWidth: 380,
            margin: 0,
          }}>Select a patient to view their details.</p>
        </div>
      );
    }

    if (detailLoading || !detail) {
      return (
        <div style={{
          padding: 'clamp(60px, 8vw, 100px) clamp(24px, 3vw, 40px)',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 400,
        }}>
          <Spinner color={P.GO} size={36} />
        </div>
      );
    }

    var patient = detail.patient;
    var intakes = detail.intakes || [];
    var bookings = detail.bookings || [];
    var latestIntake = intakes.find(function(i) { return i.status === 'completed'; });
    var summary = latestIntake && latestIntake.clinical_summary;
    var redFlags = summary && summary.red_flags;
    var hasRedFlags = redFlags && redFlags !== 'None' && redFlags.length > 0;

    return (
      <div>
        {/* Patient header + Copilot CTA */}
        <div style={{
          marginBottom: 'clamp(24px, 2.6vw, 32px)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h2 style={{
              fontFamily: FF,
              fontSize: 'clamp(24px, 2.4vw, 32px)',
              fontWeight: 400,
              color: P.INK,
              lineHeight: 1.15,
              letterSpacing: '-0.012em',
              margin: '0 0 6px',
            }}>{patientName(patient)}</h2>
            <div style={{
              fontFamily: FS,
              fontSize: 'clamp(12px, 1vw, 14px)',
              color: P.TF,
              letterSpacing: '0.02em',
            }}>
              {patient.email}
              {patient.phone ? ' \u00b7 ' + patient.phone : ''}
            </div>
          </div>
          <button
            onClick={openCopilot}
            style={{
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '12px 22px',
              background: P.OBS,
              color: P.GO2,
              border: '1px solid ' + P.GO,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background 0.2s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.background = P.OBS2; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = P.OBS; }}>
            Open Copilot
          </button>
        </div>

        {/* Red flags banner */}
        {hasRedFlags && (
          <div style={{
            padding: 'clamp(18px, 2vw, 24px)',
            background: P.RED_BG,
            border: '1px solid ' + P.RED,
            borderLeft: '3px solid ' + P.RED,
            marginBottom: 'clamp(20px, 2.4vw, 28px)',
          }}>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(10px, 0.85vw, 12px)',
              fontWeight: 700,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color: P.RED,
              marginBottom: 10,
            }}>Red flags</div>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(14px, 1.2vw, 16px)',
              color: P.TX,
              lineHeight: 1.7,
              margin: 0,
            }}>{redFlags}</p>
          </div>
        )}

        {/* Mark reviewed button (when not yet reviewed) */}
        {latestIntake && !latestIntake.reviewed_at && (
          <button
            onClick={function() { markReviewed(latestIntake.id); }}
            style={{
              width: '100%',
              fontFamily: FS,
              fontSize: 'clamp(11px, 0.95vw, 12px)',
              fontWeight: 600,
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              padding: '14px 24px',
              background: P.GO,
              color: P.OBS,
              border: 'none',
              cursor: 'pointer',
              marginBottom: 'clamp(20px, 2.4vw, 28px)',
              transition: 'background 0.2s',
            }}
            onMouseEnter={function(e) { e.currentTarget.style.background = P.GO2; }}
            onMouseLeave={function(e) { e.currentTarget.style.background = P.GO; }}>
            Mark intake as reviewed
          </button>
        )}

        {/* Reviewed status */}
        {latestIntake && latestIntake.reviewed_at && (
          <div style={{
            padding: 'clamp(12px, 1.4vw, 16px) clamp(16px, 1.8vw, 22px)',
            background: P.GREEN_BG,
            border: '1px solid ' + P.GREEN,
            borderLeft: '3px solid ' + P.GREEN,
            marginBottom: 'clamp(20px, 2.4vw, 28px)',
            textAlign: 'center',
          }}>
            <span style={{
              fontFamily: FF,
              fontSize: 'clamp(10px, 0.85vw, 12px)',
              fontWeight: 700,
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
              color: P.GREEN,
            }}>Reviewed {timeAgo(latestIntake.reviewed_at)}</span>
          </div>
        )}

        {/* Clinical summary */}
        {summary && (
          <section style={{ marginBottom: 'clamp(28px, 3vw, 40px)' }}>
            <SectionLabel>Clinical summary</SectionLabel>
            <div style={{
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
              padding: 'clamp(20px, 2.4vw, 28px)',
            }}>
              <Field label="Primary concern" value={summary.primary_concern} />
              {summary.symptoms && summary.symptoms.length > 0 && (
                <Field label="Symptoms" value={summary.symptoms.join(', ')} />
              )}
              <Field label="Duration" value={summary.duration} />
              <Field label="Menstrual status" value={summary.menstrual_status} />
              <Field label="Sleep" value={summary.sleep} />
              <Field label="Energy and mood" value={summary.energy_mood} />
              <Field label="Lifestyle" value={summary.lifestyle} />
              <Field label="Medical history" value={summary.medical_history} />
              <Field label="Medications" value={summary.medications} />
              <Field label="Family history" value={summary.family_history} />
              <Field label="Goals" value={summary.goals} />
              {summary.suggested_investigations && summary.suggested_investigations.length > 0 && (
                <Field label="Suggested investigations" value={summary.suggested_investigations.join(', ')} />
              )}
              {summary.patient_quote && (
                <div style={{
                  marginTop: 'clamp(18px, 2vw, 24px)',
                  padding: 'clamp(16px, 1.8vw, 22px) clamp(18px, 2vw, 26px)',
                  background: 'rgba(201, 169, 97, 0.06)',
                  borderLeft: '3px solid ' + P.GO,
                  fontFamily: FF,
                  fontSize: 'clamp(15px, 1.3vw, 17px)',
                  fontStyle: 'italic',
                  color: P.TX,
                  lineHeight: 1.65,
                }}>&ldquo;{summary.patient_quote}&rdquo;</div>
              )}
            </div>
          </section>
        )}

        {/* Appointments */}
        {bookings.length > 0 && (
          <section style={{ marginBottom: 'clamp(28px, 3vw, 40px)' }}>
            <SectionLabel>Appointments</SectionLabel>
            {bookings.map(function(b) {
              return (
                <div key={b.id} style={{
                  padding: 'clamp(16px, 1.8vw, 22px)',
                  background: P.PAP,
                  border: '1px solid ' + P.SAND3,
                  borderLeft: '3px solid ' + P.GO,
                  marginBottom: 10,
                }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(16px, 1.4vw, 18px)',
                    fontWeight: 500,
                    color: P.INK,
                    lineHeight: 1.25,
                    letterSpacing: '-0.005em',
                    marginBottom: 6,
                  }}>{b.consultation_type}</div>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(13px, 1.1vw, 15px)',
                    fontStyle: 'italic',
                    color: P.BR2,
                    marginBottom: 4,
                  }}>{formatDateLong(b.appointment_date)} at {b.appointment_time}</div>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(10px, 0.85vw, 11px)',
                    fontWeight: 600,
                    letterSpacing: '0.26em',
                    textTransform: 'uppercase',
                    color: P.TF,
                  }}>{b.practitioner_name} \u00b7 {b.duration_minutes} min</div>
                </div>
              );
            })}
          </section>
        )}

        {/* Conversation transcript */}
        {latestIntake && latestIntake.messages && (
          <section style={{ marginBottom: 'clamp(28px, 3vw, 40px)' }}>
            <SectionLabel>Conversation transcript</SectionLabel>
            <div style={{
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
              padding: 'clamp(20px, 2.4vw, 28px)',
              maxHeight: 460,
              overflowY: 'auto',
            }}>
              {latestIntake.messages.map(function(m, i) {
                var isUser = m.role === 'user';
                return (
                  <div key={i} style={{
                    marginBottom: 'clamp(16px, 1.6vw, 20px)',
                    paddingBottom: 'clamp(14px, 1.4vw, 18px)',
                    borderBottom: i < latestIntake.messages.length - 1 ? '1px solid ' + P.SAND3 : 'none',
                  }}>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(10px, 0.85vw, 11px)',
                      fontWeight: 700,
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      color: isUser ? P.BR2 : P.GO2,
                      marginBottom: 8,
                    }}>{isUser ? (patient.first_name || 'Patient') : 'Cleo'}</div>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(13px, 1.1vw, 14px)',
                      color: P.TX,
                      lineHeight: 1.7,
                      whiteSpace: 'pre-wrap',
                    }}>{m.content}</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // FILTER TABS
  // ────────────────────────────────────────────────────────────────────────
  function FilterTab({ active, onClick, children }) {
    return (
      <button
        onClick={onClick}
        style={{
          flex: 1,
          fontFamily: FF,
          fontSize: 'clamp(10px, 0.9vw, 12px)',
          fontWeight: 700,
          letterSpacing: '0.28em',
          textTransform: 'uppercase',
          padding: '10px 8px',
          background: active ? P.GO : 'transparent',
          color: active ? P.OBS : P.TF,
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseEnter={function(e) {
          if (!active) e.currentTarget.style.background = 'rgba(201, 169, 97, 0.08)';
        }}
        onMouseLeave={function(e) {
          if (!active) e.currentTarget.style.background = 'transparent';
        }}>
        {children}
      </button>
    );
  }

  // ════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ background: P.PAP, minHeight: '100vh', fontFamily: FF }}>

      {/* ═════════ Obsidian workstation header ═════════ */}
      <header style={{
        background: P.OBS,
        borderBottom: '1px solid ' + P.GO,
        padding: 'clamp(20px, 2.4vw, 32px) clamp(24px, 3vw, 40px)',
      }}>
        <div style={{
          maxWidth: 1400,
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(10px, 0.85vw, 12px)',
              fontWeight: 700,
              letterSpacing: '0.36em',
              textTransform: 'uppercase',
              color: P.GO2,
              marginBottom: 6,
            }}>Cleo's Secret &middot; Workstation</div>
            <h1 style={{
              fontFamily: FF,
              fontSize: 'clamp(22px, 2.4vw, 32px)',
              fontWeight: 400,
              color: P.PAP,
              lineHeight: 1.15,
              letterSpacing: '-0.008em',
              margin: 0,
            }}>Clinical <span style={{ fontStyle: 'italic', color: P.GO2 }}>dashboard.</span></h1>
          </div>

          {/* Nav buttons with badges */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <NavBtn
              onClick={function() { if (onNavigate) onNavigate('retention'); }}
              active={outreachStats.total > 0}
              urgent={outreachStats.high > 0}
              label="Outreach"
              count={outreachStats.total}
            />
            <NavBtn
              onClick={function() { if (onNavigate) onNavigate('pathology-review'); }}
              active={pendingPath > 0}
              label="Pathology"
              count={pendingPath}
            />
            <NavBtn
              onClick={function() { if (onNavigate) onNavigate('landing'); }}
              label="Exit"
            />
          </div>
        </div>
      </header>

      {/* ═════════ Main canvas ═════════ */}
      <main style={{
        maxWidth: 1400,
        margin: '0 auto',
        padding: 'clamp(28px, 3.4vw, 48px) clamp(24px, 3vw, 40px) clamp(48px, 6vw, 80px)',
      }}>

        {/* Stats strip */}
        <div style={{
          display: 'flex',
          gap: 12,
          marginBottom: 'clamp(28px, 3.4vw, 40px)',
          flexWrap: 'wrap',
        }}>
          <StatCard label="Total patients" value={stats.total} />
          <StatCard
            label="Needs review"
            value={stats.needsReview}
            highlight={stats.needsReview > 0}
            color={stats.needsReview > 0 ? P.BR2 : P.INK} />
          <StatCard label="Upcoming" value={stats.upcoming} />
          <StatCard
            label="Pathology pending"
            value={pendingPath}
            highlight={pendingPath > 0}
            onClick={function() { if (onNavigate) onNavigate('pathology-review'); }}
            subtitle={pendingPath > 0 ? 'Click to review' : null} />
          <StatCard
            label="Outreach queue"
            value={outreachStats.total}
            highlight={outreachStats.high > 0}
            color={outreachStats.high > 0 ? P.RED : P.INK}
            onClick={function() { if (onNavigate) onNavigate('retention'); }}
            subtitle={outreachStats.high > 0 ? outreachStats.high + ' urgent' : null} />
        </div>

        {/* ═════════ Pre-enrolment queue (Phase 2) ═════════ */}
        <PreEnrolmentQueue
          recs={recs}
          loading={recsLoading}
          error={recsError}
          rerunningId={rerunningId}
          onOpen={function(recId) { if (onNavigate) onNavigate('recommendation-builder', { recommendationId: recId }); }}
          onRerun={rerunAutofill}
        />

        {/* Two-pane workspace */}
        <div className="cs-clinical-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(340px, 400px) 1fr',
          gap: 'clamp(24px, 2.6vw, 36px)',
          alignItems: 'start',
        }}>

          {/* Left pane: filters + search + patient list */}
          <div>
            {/* Filter tabs */}
            <div style={{
              display: 'flex',
              gap: 2,
              marginBottom: 12,
              background: P.PAP,
              border: '1px solid ' + P.SAND3,
            }}>
              <FilterTab active={filter === 'review'} onClick={function() { setFilter('review'); }}>
                Needs review{stats.needsReview > 0 && ' (' + stats.needsReview + ')'}
              </FilterTab>
              <FilterTab active={filter === 'upcoming'} onClick={function() { setFilter('upcoming'); }}>
                Upcoming
              </FilterTab>
              <FilterTab active={filter === 'all'} onClick={function() { setFilter('all'); }}>
                All
              </FilterTab>
            </div>

            {/* Search input */}
            <input
              type="text"
              value={search}
              onChange={function(e) { setSearch(e.target.value); }}
              placeholder={'Search by name, email, or concern\u2026'}
              style={{
                width: '100%',
                padding: '12px 16px',
                fontFamily: FF,
                fontSize: 'clamp(14px, 1.2vw, 15px)',
                color: P.INK,
                background: P.PAP,
                border: '1px solid ' + P.SAND3,
                outline: 'none',
                marginBottom: 16,
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
              }}
              onFocus={function(e) { e.currentTarget.style.borderColor = P.BR; }}
              onBlur={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
            />

            {/* Patient list */}
            <div>
              {filtered.length === 0 && (
                <div style={{
                  padding: 'clamp(36px, 4vw, 56px) clamp(20px, 2.4vw, 28px)',
                  textAlign: 'center',
                  background: 'rgba(232,221,196,0.3)',
                  border: '1px dashed ' + P.SAND3,
                }}>
                  <p style={{
                    fontFamily: FF,
                    fontSize: 'clamp(15px, 1.3vw, 17px)',
                    fontStyle: 'italic',
                    color: P.BR2,
                    margin: 0,
                  }}>No patients match this filter.</p>
                </div>
              )}
              {filtered.map(function(p) { return <PatientCard key={p.id} patient={p} />; })}
            </div>
          </div>

          {/* Right pane: detail */}
          <div className="cs-clinical-detail" style={{
            position: 'sticky',
            top: 24,
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
            paddingLeft: 'clamp(20px, 2.2vw, 28px)',
            borderLeft: '1px solid ' + P.SAND3,
          }}>
            {renderDetail()}
          </div>

        </div>
      </main>

      {/* Responsive */}
      <style>{`
        @media (max-width: 900px) {
          .cs-clinical-grid {
            grid-template-columns: 1fr !important;
          }
          .cs-clinical-detail {
            position: static !important;
            border-left: none !important;
            border-top: 1px solid ${P.SAND3};
            padding-left: 0 !important;
            padding-top: clamp(20px, 2.4vw, 28px);
            max-height: none !important;
          }
        }
      `}</style>
    </div>
  );
}

// ───────── NavBtn — header quick-jump button with optional count badge ─────────
function NavBtn({ onClick, active, urgent, label, count }) {
  // urgent → red bg, active → gold bg, default → ghost outline
  var bg = 'transparent';
  var color = P.GO2;
  var borderCol = P.GO2;
  if (urgent) {
    bg = P.RED;
    color = P.PAP;
    borderCol = P.RED;
  } else if (active) {
    bg = P.GO;
    color = P.OBS;
    borderCol = P.GO;
  }

  return (
    <button
      onClick={onClick}
      style={{
        fontFamily: FS,
        fontSize: 'clamp(11px, 0.9vw, 12px)',
        fontWeight: 600,
        letterSpacing: '0.24em',
        textTransform: 'uppercase',
        padding: '12px 22px',
        background: bg,
        color: color,
        border: '1px solid ' + borderCol,
        cursor: 'pointer',
        transition: 'all 0.2s',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={function(e) {
        if (urgent) { e.currentTarget.style.background = '#A82B1F'; }
        else if (active) { e.currentTarget.style.background = P.GO2; }
        else { e.currentTarget.style.background = 'rgba(224, 198, 133, 0.1)'; }
      }}
      onMouseLeave={function(e) {
        e.currentTarget.style.background = bg;
      }}>
      {label}{count > 0 && ' (' + count + ')'}
    </button>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// PreEnrolmentQueue — Phase 2 section above the patient list.
// Shows draft program_recommendations awaiting GP review. Each card opens
// the RecommendationBuilder. If autofill_error is set, a Rerun affordance
// re-triggers /api/recommendation-autofill with the pattern_assignment_id.
// ═════════════════════════════════════════════════════════════════════════
function PreEnrolmentQueue({ recs, loading, error, rerunningId, onOpen, onRerun }) {
  var count = (recs || []).length;

  return (
    <section style={{
      marginBottom: 'clamp(28px, 3.4vw, 40px)',
      padding: 'clamp(24px, 2.6vw, 32px)',
      background: P.SAND,
      border: '1px solid ' + P.SAND3,
      borderTop: '3px solid ' + P.GO,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 18,
        marginBottom: 'clamp(18px, 2vw, 24px)',
        paddingBottom: 'clamp(14px, 1.6vw, 18px)',
        borderBottom: '1px solid ' + P.SAND3,
      }}>
        <div>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 6,
          }}>Pre-enrolment queue</div>
          <div style={{
            fontFamily: FF,
            fontStyle: 'italic',
            fontSize: 'clamp(18px, 1.8vw, 22px)',
            color: P.INK,
            lineHeight: 1.3,
          }}>
            {loading ? 'Loading recommendations.' :
              count === 0 ? 'No draft recommendations awaiting review.' :
              count === 1 ? '1 draft recommendation awaiting your review.' :
              count + ' draft recommendations awaiting your review.'}
          </div>
        </div>
        {count > 0 && (
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(22px, 2.4vw, 30px)',
            color: P.INK,
            letterSpacing: '-0.01em',
          }}>{count}</div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          marginBottom: 14,
          padding: '10px 14px',
          background: P.RED_BG,
          border: '1px solid ' + P.RED,
          fontFamily: FF,
          fontStyle: 'italic',
          fontSize: 14,
          color: P.RED,
        }}>{error}</div>
      )}

      {/* Loading state */}
      {loading && (
        <div style={{ padding: 'clamp(28px, 3vw, 40px)', textAlign: 'center' }}>
          <Spinner color={P.BR2} size={28} />
        </div>
      )}

      {/* Empty state */}
      {!loading && count === 0 && !error && (
        <div style={{
          padding: 'clamp(28px, 3vw, 40px)',
          textAlign: 'center',
          fontFamily: FF,
          fontStyle: 'italic',
          fontSize: 'clamp(15px, 1.3vw, 17px)',
          color: P.TF,
        }}>
          When you approve a pattern assignment, a draft recommendation will appear here for review.
        </div>
      )}

      {/* Cards */}
      {!loading && count > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(420px, 100%), 1fr))',
          gap: 'clamp(14px, 1.4vw, 18px)',
        }}>
          {recs.map(function(rec) {
            return (
              <RecQueueCard
                key={rec.id}
                rec={rec}
                rerunning={rerunningId === rec.id}
                onOpen={onOpen}
                onRerun={onRerun}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

function RecQueueCard({ rec, rerunning, onOpen, onRerun }) {
  var patient = rec.patient || {};
  var pattern = rec.pattern || {};
  var pkg = rec.package || {};
  var patientLabel = (patient.first_name || patient.last_name)
    ? ((patient.first_name || '') + ' ' + (patient.last_name || '')).trim()
    : (patient.email || 'Unknown patient');

  return (
    <article
      onClick={function() { onOpen(rec.id); }}
      style={{
        padding: 'clamp(18px, 1.8vw, 22px)',
        background: P.PAP,
        border: '1px solid ' + P.SAND3,
        cursor: 'pointer',
        transition: 'background 0.2s, border-color 0.2s',
      }}
      onMouseEnter={function(e) {
        e.currentTarget.style.background = 'rgba(232,221,196,0.5)';
        e.currentTarget.style.borderColor = P.GO;
      }}
      onMouseLeave={function(e) {
        e.currentTarget.style.background = P.PAP;
        e.currentTarget.style.borderColor = P.SAND3;
      }}>

      {/* Patient name + service count */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 12,
        marginBottom: 8,
      }}>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(18px, 1.6vw, 21px)',
          fontWeight: 500,
          color: P.INK,
          lineHeight: 1.2,
        }}>{patientLabel}</div>
        <div style={{
          fontFamily: FS,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: P.TF,
          whiteSpace: 'nowrap',
        }}>{rec.services_count} service{rec.services_count === 1 ? '' : 's'}</div>
      </div>

      {/* Pattern + package metadata */}
      <div style={{
        fontFamily: FF,
        fontSize: 14,
        color: P.TL,
        marginBottom: 12,
        lineHeight: 1.55,
      }}>
        {pattern.name ? pattern.name + ' pattern' : 'Pattern unknown'}
        {pkg.display_name ? ' · ' + pkg.display_name : ''}
      </div>

      {/* Timestamp */}
      <div style={{
        fontFamily: FF,
        fontStyle: 'italic',
        fontSize: 13,
        color: P.BR2,
        marginBottom: 12,
      }}>Drafted {timeAgo(rec.created_at)}</div>

      {/* Autofill error banner if present */}
      {rec.autofill_error && (
        <div style={{
          padding: '10px 12px',
          marginBottom: 12,
          background: P.RED_BG,
          border: '1px solid ' + P.RED,
        }}>
          <div style={{
            fontFamily: FS,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: P.RED,
            marginBottom: 4,
          }}>Autofill error</div>
          <div style={{
            fontFamily: FF,
            fontStyle: 'italic',
            fontSize: 13,
            color: P.RED,
            lineHeight: 1.5,
          }}>{rec.autofill_error}</div>
        </div>
      )}

      {/* Actions row */}
      <div style={{
        display: 'flex',
        gap: 10,
        marginTop: 4,
        alignItems: 'center',
      }}>
        <button
          onClick={function(e) { e.stopPropagation(); onOpen(rec.id); }}
          style={{
            padding: '10px 18px',
            fontFamily: FS,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            background: P.GO,
            color: P.OBS,
            border: '1px solid ' + P.GO,
            cursor: 'pointer',
          }}>Open builder</button>

        {rec.pattern_assignment_id && (
          <button
            onClick={function(e) { e.stopPropagation(); onRerun(rec.pattern_assignment_id, rec.id); }}
            disabled={rerunning}
            style={{
              padding: '10px 14px',
              fontFamily: FS,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              background: 'transparent',
              color: P.TF,
              border: '1px solid ' + P.SAND3,
              cursor: rerunning ? 'wait' : 'pointer',
              opacity: rerunning ? 0.6 : 1,
            }}>{rerunning ? 'Rerunning' : 'Rerun autofill'}</button>
        )}
      </div>
    </article>
  );
}
